import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAbstractEdit } from '@/module/Author/Abstract/model/AbstractQuery';
import { AbstractSchema, abstractSchema, defaultAbstractValues } from '@/module/Author/Abstract/model/AbstractSchema';
import { defaultIdentifiers } from '@/module/Author/Publication/model/PublicationTypes';

import { useAppNotification } from '@feature/utils';

import { AbstractForm } from './AbstractForm';
import { Abstract } from './model/AbstractTypes';

interface AbstractEditProps {
  abstract: Abstract;
}

const convert = (abstract: Abstract): AbstractSchema => ({
  ...abstract,
  identifiers: abstract.identifiers ?? defaultIdentifiers,
});

export function AbstractEdit(props: AbstractEditProps) {
  const { abstract } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useAbstractEdit(abstract.id);
  const notification = useAppNotification();

  const onSubmit = (values: AbstractSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(abstract), [abstract]);
  const navigate = useNavigate();

  return (
    <AbstractForm
      title={t('abstracts.form.title.edit')}
      schema={abstractSchema}
      defaultValues={defaultAbstractValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={abstract.source.type === 'SCOPUS'}
    />
  );
}
